// Подвкладки раздела («Себестоимость»: Обзор | ABC-анализ, «Кухня»: Заказы | Стоп-лист). Ставятся в закреплённую
// панель фильтров каждой вкладки: на десктопе — в строку с выбором ресторана (36 px), на телефоне — во всю ширину (44 px).
// Цвет — акцент раздела (SECTION_ACCENTS).
// variant="filter" — тот же переключатель для фильтров («Всё | Только кухня»): нейтральный индикатор без акцента,
// без иконок, radiogroup/radio вместо вкладок и панелей.
//
// Активную вкладку подсвечивает один «тянущийся» индикатор: позиция — left/right от краёв дорожки, ведущий край
// едет первым, второй — с задержкой (indicatorTransition). Это осознанное исключение из «только transform и opacity»:
// элемент один и маленький. Первый рендер, смена ширины и prefers-reduced-motion — без анимации.
//
// Переключатель живёт внутри вкладки, поэтому при переключении появляется другой экземпляр: на «Кухне» вкладка
// монтируется заново, в «Себестоимости» показывается копия из другой вкладки. Чтобы индикатор всё равно тянулся
// (и фокус после стрелок не терялся), последнее переключение запоминается по группе (controls) на SWITCH_HANDOFF_MS:
// новый экземпляр начинает анимацию со старой вкладки.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { MOTION, REDUCED_MOTION } from '../styles/tokens';

const INDICATOR_MS = 260;
const TRAILING_DELAY_MS = 90;
const SWITCH_HANDOFF_MS = 400;
const INACTIVE_COLOR = '#a1a1aa';
const FILTER_INDICATOR = 'rgba(255,255,255,0.10)';
const FADE = 24; // px затухания края при прокрутке

// Последние переключения по группе: { from, to, at, keyboard }
const lastSwitch = new Map();

export const tabDomId = (id, value) => `${id}-tab-${value}`;
export const panelDomId = (controls, value) => `${controls}-${value}`;

// Ведущий край — без задержки, второй — с задержкой: вправо ведёт right, влево — left
export function indicatorTransition(fromIndex, toIndex) {
  const right = toIndex > fromIndex;
  const edge = (side, delay) => `${side} ${INDICATOR_MS}ms ${MOTION.easing} ${delay}ms`;
  return right
    ? `${edge('right', 0)}, ${edge('left', TRAILING_DELAY_MS)}`
    : `${edge('left', 0)}, ${edge('right', TRAILING_DELAY_MS)}`;
}

// Следующая вкладка по клавише (по кругу); null — клавиша не наша
export function nextTabIndex(key, index, count) {
  if (key === 'ArrowRight') return (index + 1) % count;
  if (key === 'ArrowLeft') return (index - 1 + count) % count;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
}

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * tabs — [{ value, label, icon? }] (icon — компонент иконки MUI, необязателен; у фильтра не рисуется).
 * id — префикс id вкладок этого экземпляра; controls — префикс id панелей (role="tabpanel"), он же группа
 * для передачи переключения между экземплярами. accent — SECTION_ACCENTS раздела (фильтру не нужен).
 * fullWidth — на телефоне во всю ширину, пункты поровну; false — по содержимому (в строке с другими элементами).
 * iconsFrom — ширина окна (px), с которой видны иконки: уже — только подписи, чтобы вкладки влезли в строку.
 */
export default function SegmentedTabs({
  id, controls, tabs, value, onChange, accent, label, variant = 'tabs', fullWidth = true, iconsFrom = 0,
}) {
  const filter = variant === 'filter';
  const group = controls || id;
  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const indicatorRef = useRef(null);
  const tabRefs = useRef({});
  const targetRef = useRef(null); // последняя выставленная позиция индикатора
  const prevValueRef = useRef(null);
  const [fade, setFade] = useState({ left: false, right: false });

  const measure = useCallback((tabValue) => {
    const track = trackRef.current;
    const tab = tabRefs.current[tabValue];
    if (!track || !tab || track.offsetWidth === 0) return null; // скрыт (неактивная вкладка «Себестоимости»)
    return { left: tab.offsetLeft, right: track.offsetWidth - tab.offsetLeft - tab.offsetWidth };
  }, []);

  const place = useCallback((pos, transition) => {
    const el = indicatorRef.current;
    if (!el || !pos) return;
    el.style.transition = transition || 'none';
    el.style.left = `${pos.left}px`;
    el.style.right = `${pos.right}px`;
    el.style.opacity = '1';
    targetRef.current = pos;
  }, []);

  // Со старой вкладки на новую: старая позиция без перехода, затем новая с переходом
  const slide = useCallback((fromValue, toValue) => {
    const to = measure(toValue);
    if (!to) return false;
    const from = fromValue != null && fromValue !== toValue ? measure(fromValue) : null;
    if (!from || reducedMotion()) {
      place(to);
      return true;
    }
    const indexOf = (v) => tabs.findIndex((t) => t.value === v);
    place(from);
    void indicatorRef.current.offsetWidth; // зафиксировать старую позицию до перехода
    place(to, indicatorTransition(indexOf(fromValue), indexOf(toValue)));
    return true;
  }, [measure, place, tabs]);

  const updateFade = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setFade((f) => (f.left === left && f.right === right ? f : { left, right }));
  }, []);

  // Активная вкладка — в видимую часть прокрутки
  const revealActive = useCallback(() => {
    const scroller = scrollRef.current;
    const tab = tabRefs.current[value];
    if (!scroller || !tab || scroller.scrollWidth <= scroller.clientWidth) return;
    const start = tab.offsetLeft - FADE;
    const end = tab.offsetLeft + tab.offsetWidth + FADE - scroller.clientWidth;
    const left = scroller.scrollLeft > start ? start : scroller.scrollLeft < end ? end : null;
    if (left !== null) scroller.scrollTo({ left: Math.max(0, left), behavior: reducedMotion() ? 'auto' : 'smooth' });
  }, [value]);

  // Смена вкладки (и первый показ): тянем индикатор; фокус после стрелок — на новую вкладку
  useLayoutEffect(() => {
    const prev = prevValueRef.current;
    const handoff = group ? lastSwitch.get(group) : null;
    const recent = handoff && handoff.to === value && Date.now() - handoff.at < SWITCH_HANDOFF_MS;
    // Только что переключили — со старой вкладки (этот экземпляр мог быть скрыт или только смонтирован);
    // первый показ без переключения — сразу на месте
    const from = recent ? handoff.from : prev;
    if (slide(from, value)) {
      prevValueRef.current = value;
      if (recent && handoff.keyboard && !trackRef.current.contains(document.activeElement)) {
        tabRefs.current[value]?.focus({ preventScroll: true });
      }
      revealActive();
    }
    // скрытый экземпляр позицию не меняет: при показе потянется с той вкладки, что была
  }, [value, group, slide, revealActive]);

  // Смена ширины — без анимации
  useEffect(() => {
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      const pos = measure(value);
      const last = targetRef.current;
      if (pos && (!last || last.left !== pos.left || last.right !== pos.right)) {
        place(pos);
        prevValueRef.current = value;
      }
      updateFade();
    });
    observer.observe(track);
    if (scrollRef.current) observer.observe(scrollRef.current);
    return () => observer.disconnect();
  }, [value, measure, place, updateFade]);

  const select = (next, keyboard) => {
    if (next === value) return;
    if (group) lastSwitch.set(group, { from: value, to: next, at: Date.now(), keyboard });
    onChange(next);
  };

  const onKeyDown = (event) => {
    const index = tabs.findIndex((t) => t.value === value);
    const next = nextTabIndex(event.key, index, tabs.length);
    if (next === null) return;
    event.preventDefault();
    const nextValue = tabs[next].value;
    tabRefs.current[nextValue]?.focus({ preventScroll: true });
    select(nextValue, true);
  };

  const mask = fade.left || fade.right
    ? `linear-gradient(90deg, ${fade.left ? 'transparent' : '#000'}, #000 ${FADE}px, #000 calc(100% - ${FADE}px), ${fade.right ? 'transparent' : '#000'})`
    : null;

  return (
    <Box
      ref={scrollRef}
      onScroll={updateFade}
      sx={{
        display: { xs: fullWidth ? 'flex' : 'inline-flex', md: 'inline-flex' },
        flexShrink: 0,
        width: { xs: fullWidth ? '100%' : 'auto', md: 'auto' },
        maxWidth: '100%',
        boxSizing: 'border-box',
        p: '4px',
        borderRadius: '12px',
        backgroundColor: 'rgba(255,255,255,0.04)',
        // рамка тенью — не добавляет высоты: 36 px на десктопе, 44 px на телефоне
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        ...(mask && { maskImage: mask, WebkitMaskImage: mask }),
      }}
    >
      <Box
        ref={trackRef}
        role={filter ? 'radiogroup' : 'tablist'}
        aria-label={label}
        onKeyDown={onKeyDown}
        sx={{
          position: 'relative',
          display: 'flex',
          gap: '2px',
          width: { xs: 'max-content', md: 'auto' },
          minWidth: { xs: '100%', md: 0 },
        }}
      >
        <Box
          ref={indicatorRef}
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            opacity: 0, // до первого замера
            borderRadius: '9px',
            backgroundColor: filter ? FILTER_INDICATOR : `rgba(${accent.rgb},0.18)`,
            pointerEvents: 'none',
            [REDUCED_MOTION]: { transition: 'none !important' },
          }}
        />
        {tabs.map((tab) => {
          const active = tab.value === value;
          const Icon = filter ? null : tab.icon;
          const activeColor = filter ? '#fff' : accent.tab;
          const a11y = filter
            ? { role: 'radio', 'aria-checked': active }
            : { id: tabDomId(id, tab.value), role: 'tab', 'aria-selected': active, 'aria-controls': panelDomId(controls, tab.value) };
          return (
            <ButtonBase
              key={String(tab.value)}
              ref={(el) => { tabRefs.current[tab.value] = el; }}
              {...a11y}
              tabIndex={active ? 0 : -1}
              onClick={() => select(tab.value, false)}
              sx={{
                position: 'relative', // над индикатором
                flex: { xs: fullWidth ? '1 1 0' : '0 0 auto', md: '0 0 auto' },
                minWidth: { xs: 'max-content', md: 0 },
                height: { xs: 36, md: 28 },
                px: 2,
                gap: '7px',
                borderRadius: '9px',
                fontSize: '14px',
                fontWeight: active ? 700 : 600,
                whiteSpace: 'nowrap',
                color: active ? activeColor : INACTIVE_COLOR,
                backgroundColor: 'transparent',
                ...(!active && { '@media (hover: hover)': { '&:hover': { color: '#fff' } } }),
                '&.Mui-focusVisible': { outline: `2px solid ${activeColor}`, outlineOffset: '2px' },
              }}
            >
              {Icon && (
                <Icon
                  aria-hidden="true"
                  sx={{
                    fontSize: 16,
                    color: 'currentColor',
                    ...(iconsFrom > 0 && { [`@media (max-width: ${iconsFrom - 0.02}px)`]: { display: 'none' } }),
                  }}
                />
              )}
              {/* ширина под жирный текст заранее: смена начертания не сдвигает вкладки и индикатор */}
              <Box
                component="span"
                data-label={tab.label}
                sx={{
                  display: 'inline-flex',
                  flexDirection: 'column',
                  '&::after': { content: 'attr(data-label)', fontWeight: 700, height: 0, overflow: 'hidden', visibility: 'hidden' },
                }}
              >
                {tab.label}
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}
